import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import { formatCurrency } from '../utils/currency';
import './EmployeeDashboard.css';

function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employeeData, setEmployeeData] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [pendingTimesheets, setPendingTimesheets] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeDashboardData();
  }, []);

  const fetchEmployeeDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch employee profile
      const employeeRes = await axios.get('/employees/my-profile');
      setEmployeeData(employeeRes.data.data || employeeRes.data);
      
      // Fetch assigned projects (limit to 5 recent)
      try {
        const projectsRes = await axios.get('/projects?size=5');
        const projects = projectsRes.data.data?.content || projectsRes.data.content || [];
        setRecentProjects(projects.slice(0, 5));
      } catch (err) {
        console.error('Error fetching projects:', err);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee dashboard data:', error);
      setLoading(false);
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="employee-dashboard">
        <div className="loading">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="employee-dashboard">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-content">
          <h1>Welcome back, {user?.name || 'Employee'}! 👋</h1>
          <p className="welcome-subtitle">Here's your overview for today</p>
        </div>
        <div className="current-date">
          {new Date().toLocaleDateString('en-IN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👤</div>
          <div className="stat-content">
            <div className="stat-label">Employee ID</div>
            <div className="stat-value">{employeeData?.employeeCode || 'N/A'}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💼</div>
          <div className="stat-content">
            <div className="stat-label">Designation</div>
            <div className="stat-value">{employeeData?.designation || 'N/A'}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
            <div className="stat-label">Department</div>
            <div className="stat-value">{employeeData?.department || 'N/A'}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-label">Join Date</div>
            <div className="stat-value">{formatDate(employeeData?.joinDate)}</div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content-grid">
        {/* Profile Card */}
        <div className="dashboard-card profile-card">
          <div className="card-header">
            <h2>My Profile</h2>
          </div>
          <div className="card-body">
            <div className="profile-info">
              <div className="profile-avatar">
                {user?.name?.charAt(0).toUpperCase() || 'E'}
              </div>
              <div className="profile-details">
                <h3>{user?.name}</h3>
                <p className="profile-email">{user?.email || 'N/A'}</p>
                <p className="profile-role">
                  <span className="role-badge">{user?.customRoleName || user?.role || 'EMPLOYEE'}</span>
                </p>
              </div>
            </div>
            {employeeData && (
              <div className="profile-extra-info">
                <div className="info-row">
                  <span className="info-label">Base Salary:</span>
                  <span className="info-value">{formatCurrency(employeeData.baseSalary)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className={`status-badge ${employeeData.active ? 'active' : 'inactive'}`}>
                    {employeeData.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Projects */}
        <div className="dashboard-card projects-card">
          <div className="card-header">
            <h2>Recent Projects</h2>
          </div>
          <div className="card-body">
            {recentProjects.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <p>No projects available</p>
              </div>
            ) : (
              <div className="projects-list">
                {recentProjects.map(project => (
                  <div key={project.id} className="project-item">
                    <div className="project-info">
                      <div className="project-name">{project.name}</div>
                      <div className="project-meta">
                        <span className={`project-status status-${project.status?.toLowerCase()}`}>
                          {project.status}
                        </span>
                        <span className="project-budget">{formatCurrency(project.budget)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card actions-card">
          <div className="card-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="card-body">
            <div className="actions-grid">
              <button className="action-button" onClick={() => navigate('/timesheets/my')}>
                <span className="action-icon">⏰</span>
                <span className="action-label">Submit Timesheet</span>
              </button>
              <button className="action-button" onClick={() => navigate('/projects')}>
                <span className="action-icon">📊</span>
                <span className="action-label">View Projects</span>
              </button>
              <button className="action-button" onClick={() => navigate('/supervisor-updates/my')}>
                <span className="action-icon">📋</span>
                <span className="action-label">Site Updates</span>
              </button>
              <button className="action-button" onClick={() => navigate('/calendar')}>
                <span className="action-icon">📅</span>
                <span className="action-label">Calendar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Announcements/Info */}
        <div className="dashboard-card info-card">
          <div className="card-header">
            <h2>Important Information</h2>
          </div>
          <div className="card-body">
            <div className="info-list">
              <div className="info-item">
                <div className="info-item-icon">ℹ️</div>
                <div className="info-item-content">
                  <div className="info-item-title">Company Policy</div>
                  <div className="info-item-text">
                    Please ensure timesheets are submitted by end of each week
                  </div>
                </div>
              </div>
              <div className="info-item">
                <div className="info-item-icon">📢</div>
                <div className="info-item-content">
                  <div className="info-item-title">Reminder</div>
                  <div className="info-item-text">
                    Contact your supervisor for project updates and queries
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDashboard;
